import {Button, DataStatus, openConfirmDialog} from 'platform/components';
import {Box, Heading, HStack, Text, VStack, Show} from 'platform/foundation';

import {
  useListServiceAccountKeysQuery,
  useRevokeServiceAccountKeyMutation,
  useRotateServiceAccountKeyMutation,
} from '@dms/api/security';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {openServiceAccountKeyResultDialog} from '../utils/openServiceAccountKeyResultDialog';
import {ServiceAccountKeyCard} from './ServiceAccountKeyCard';

interface ServiceAccountKeysTabProps extends RequiredTestIdProps {
  serviceAccountId: string;
  canRotate: boolean;
  canRevoke: boolean;
}

export function ServiceAccountKeysTab(props: ServiceAccountKeysTabProps) {
  const {data, isLoading, isError} = useListServiceAccountKeysQuery({
    serviceAccountId: props.serviceAccountId,
  });
  const [rotateServiceAccountKey] = useRotateServiceAccountKeyMutation();
  const [revokeServiceAccountKey] = useRevokeServiceAccountKeyMutation();

  const handleRotate = () =>
    openConfirmDialog({
      text: i18n.t('entity.serviceAccount.labels.rotateKeyConfirm'),
      'data-testid': suffixTestId('rotateConfirm', props),
      onConfirm: () =>
        rotateServiceAccountKey({serviceAccountId: props.serviceAccountId})
          .unwrap()
          .then((response) =>
            openServiceAccountKeyResultDialog({
              title: i18n.t('entity.serviceAccount.labels.rotatedTitle'),
              keyId: response.keyId,
              privateKeyPem: response.privateKeyPem,
            })
          )
          .catch(handleApiError),
    });

  const handleRevoke = (keyId: string) =>
    openConfirmDialog({
      text: i18n.t('entity.serviceAccount.labels.revokeKeyConfirm'),
      'data-testid': suffixTestId('revokeConfirm', props),
      onConfirm: () =>
        revokeServiceAccountKey({serviceAccountId: props.serviceAccountId, keyId})
          .unwrap()
          .catch(handleApiError),
    });

  return (
    <VStack spacing={4}>
      <HStack align="center" spacing={4}>
        <VStack spacing={1}>
          <Heading size={4}>{i18n.t('entity.serviceAccount.labels.keys')}</Heading>
          <Text size="small" color="secondary">
            {i18n.t('entity.serviceAccount.labels.keysSubtitle')}
          </Text>
        </VStack>
        <Box flex={1} />
        <Show when={props.canRotate}>
          <Button
            variant="primary"
            onClick={handleRotate}
            title={i18n.t('entity.serviceAccount.actions.rotateKey')}
            data-testid={suffixTestId('rotate', props)}
          />
        </Show>
      </HStack>
      <DataStatus
        isLoading={isLoading}
        isError={isError}
        isEmpty={(data?.keys.length ?? 0) === 0}
        minHeight={40}
      >
        <VStack spacing={3}>
          {(data?.keys ?? []).map((serviceAccountKey) => (
            <ServiceAccountKeyCard
              key={serviceAccountKey.id}
              serviceAccountKey={serviceAccountKey}
              canRevoke={props.canRevoke}
              onRevoke={() => handleRevoke(serviceAccountKey.id)}
              data-testid={suffixTestId(`key-[${serviceAccountKey.id}]`, props)}
            />
          ))}
        </VStack>
      </DataStatus>
    </VStack>
  );
}
